/**
 * DataList constants and default values.
 *
 * Centralises default configuration, API parameter names, display mappings,
 * and i18n keys used across the DataList component tree.
 */

import type { DisplayConfig, DataListI18nOverrides } from './types';

// ---------------------------------------------------------------------------
// Pagination Defaults
// ---------------------------------------------------------------------------

/** Default number of items per page. */
export const DEFAULT_PAGE_SIZE = 25 as const;

/** Default options for the page-size selector. */
export const DEFAULT_PAGE_SIZE_OPTIONS: readonly number[] = [
  10, 25, 50, 100,
] as const;

// ---------------------------------------------------------------------------
// Search Defaults
// ---------------------------------------------------------------------------

/** Default debounce delay (ms) before a search term triggers a query. */
export const DEFAULT_SEARCH_DEBOUNCE_MS = 300 as const;

// ---------------------------------------------------------------------------
// API Parameter Name Defaults
// ---------------------------------------------------------------------------

/** Default query-parameter name for the sort column. */
export const DEFAULT_SORT_PARAM = 'sort_by' as const;

/** Default query-parameter name for the sort direction. */
export const DEFAULT_DIRECTION_PARAM = 'sort_dir' as const;

/** Default query-parameter name for the page number. */
export const DEFAULT_PAGE_PARAM = 'page' as const;

/** Default query-parameter name for the page size. */
export const DEFAULT_PAGE_SIZE_PARAM = 'per_page' as const;

/** Default query-parameter name for the search term. */
export const DEFAULT_SEARCH_PARAM = 'search' as const;

// ---------------------------------------------------------------------------
// Display Defaults
// ---------------------------------------------------------------------------

/**
 * Default display configuration applied when no display options are
 * specified in the DataListConfig.
 */
export const DEFAULT_DISPLAY: Required<
  Pick<
    DisplayConfig,
    | 'collapsible'
    | 'defaultCollapsed'
    | 'size'
    | 'stickyHeader'
    | 'striped'
    | 'hoverable'
  >
> = {
  collapsible: false,
  defaultCollapsed: false,
  size: 'default',
  stickyHeader: false,
  striped: false,
  hoverable: true,
} as const;

// ---------------------------------------------------------------------------
// Column Defaults
// ---------------------------------------------------------------------------

/** Default column type when none is specified. */
export const COLUMN_TYPE_DEFAULT = 'text' as const;

/**
 * Sort direction cycle applied when a column header is clicked.
 * `none` -> `asc` -> `desc` -> `none`
 */
export const SORT_CYCLE: readonly ('none' | 'asc' | 'desc')[] = [
  'none',
  'asc',
  'desc',
] as const;

// ---------------------------------------------------------------------------
// Manual row order (data-row-ordering Tier 2)
// ---------------------------------------------------------------------------

/** Default field name carrying each row's stable business id. */
export const DEFAULT_ROW_ID_FIELD = 'id' as const;

/**
 * Surfaced message when a tenant authored a manual row order (`reorderRows`)
 * but the DataList source is server-paginated / streamed (an `endpoint`).
 * Manual ordering is only sensible for a small, fully-loaded inline `data[]`
 * set, so for paginated sources the feature is a **documented no-op**: the
 * surface shows this message rather than silently misordering a single page.
 */
export const MANUAL_ROW_ORDER_UNAVAILABLE_MESSAGE =
  'Manual row ordering is unavailable for server-paginated data; the configured order was ignored.' as const;

// ---------------------------------------------------------------------------
// Default i18n Keys
// ---------------------------------------------------------------------------

/**
 * Default i18n key patterns used when no overrides are provided.
 * These follow a consistent `dataList.<token>` namespace.
 */
export const DEFAULT_I18N_KEYS: Required<
  Pick<
    DataListI18nOverrides,
    | 'emptyMessage'
    | 'errorMessage'
    | 'loadingMessage'
    | 'searchPlaceholder'
    | 'totalLabel'
    | 'itemsCount'
    | 'exportLabel'
    | 'createLabel'
    | 'selectAllLabel'
    | 'bulkActionsLabel'
  >
> = {
  emptyMessage: 'No data available',
  errorMessage: 'An error occurred while loading data',
  loadingMessage: 'Loading...',
  searchPlaceholder: 'Search...',
  totalLabel: 'Total',
  itemsCount: '',
  exportLabel: 'Export',
  createLabel: 'Create',
  selectAllLabel: 'Select all',
  bulkActionsLabel: 'Actions',
} as const;

// ---------------------------------------------------------------------------
// Boolean Column Defaults
// ---------------------------------------------------------------------------

/** Default icons for boolean column rendering. */
export const DEFAULT_BOOLEAN_ICONS = {
  true: 'check',
  false: 'x',
} as const;

/** Default colours for boolean column rendering. */
export const DEFAULT_BOOLEAN_COLORS = {
  true: 'success',
  false: 'muted',
} as const;

// ---------------------------------------------------------------------------
// Text Transform CSS Class Mapping
// ---------------------------------------------------------------------------

/** Maps text-transform options to Tailwind CSS utility classes. */
export const TEXT_TRANSFORM_CLASSES: Record<
  'lowercase' | 'capitalize' | 'uppercase' | 'none',
  string
> = {
  lowercase: 'lowercase',
  capitalize: 'capitalize',
  uppercase: 'uppercase',
  none: '',
} as const;

// ---------------------------------------------------------------------------
// Text Alignment CSS Class Mapping
// ---------------------------------------------------------------------------

/** Maps text-align options to Tailwind CSS utility classes. */
export const ALIGN_CLASSES: Record<'left' | 'center' | 'right', string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} as const;

// ---------------------------------------------------------------------------
// Responsive Breakpoint
// ---------------------------------------------------------------------------

/** Breakpoint (px) below which the table switches to a mobile card layout. */
export const MOBILE_BREAKPOINT = 768 as const;
